/**
 * Cart Page Component
 * Handles quantity updates, discount codes, order notes, and free shipping progress
 * ============================================
 */

(function() {
  'use strict';

  const UPDATE_DELAY = 500; // Debounce delay in ms

  class CartPage {
    constructor() {
      this.cartPage = document.querySelector('[data-cart-page]');
      if (!this.cartPage) return;

      this.config = window.cartPageConfig || {};
      this.spendingGoalThreshold = this.config.spendingGoalThreshold || 0;
      this.goalName = this.config.goalName || 'Free Shipping';
      this.moneyFormat = this.config.moneyFormat || '${{amount}}';
      
      this.updateTimeout = null;
      this.init();
    }

    init() {
      this.bindQuantityControls();
      this.bindRemoveButtons();
      this.bindNotesToggle();
      this.bindNotesTextarea();
      this.bindOffersToggle();
      this.listenForCartUpdates();
    }

    /**
     * Quantity Controls
     */
    bindQuantityControls() {
      // Increase/Decrease buttons
      this.cartPage.querySelectorAll('[data-qty-decrease], [data-qty-increase]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleQuantityButton(e));
      });

      // Direct input changes
      this.cartPage.querySelectorAll('[data-qty-input]').forEach(input => {
        input.addEventListener('change', (e) => this.handleQuantityInput(e));
      });
    }

    handleQuantityButton(e) {
      const btn = e.currentTarget;
      const item = btn.closest('[data-cart-item]');
      const input = item.querySelector('[data-qty-input]');
      const key = btn.dataset.lineKey;
      let value = parseInt(input.value) || 1;

      if (btn.hasAttribute('data-qty-decrease')) {
        value = Math.max(1, value - 1);
      } else {
        value++;
      }

      input.value = value;
      this.updateDecreaseButtonState(item, value);
      this.debouncedUpdate(key, value, item);
    }

    handleQuantityInput(e) {
      const input = e.currentTarget;
      const item = input.closest('[data-cart-item]');
      const key = input.dataset.lineKey;
      let value = parseInt(input.value) || 1;
      
      if (value < 1) value = 1;
      input.value = value;

      this.updateDecreaseButtonState(item, value);
      this.debouncedUpdate(key, value, item);
    }

    updateDecreaseButtonState(item, value) {
      const decreaseBtn = item.querySelector('[data-qty-decrease]');
      if (decreaseBtn) {
        decreaseBtn.disabled = value <= 1;
      }
    }

    debouncedUpdate(key, quantity, item) {
      clearTimeout(this.updateTimeout);
      
      // Show loading state
      item.classList.add('is-loading');
      
      this.updateTimeout = setTimeout(async () => {
        await this.updateQuantity(key, quantity, item);
      }, UPDATE_DELAY);
    }

    async updateQuantity(key, quantity, item) {
      try {
        let cart;
        
        if (window.CartAPI) {
          cart = await window.CartAPI.updateItem(key, quantity);
        } else {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: quantity })
          });
          
          if (!response.ok) throw new Error('Failed to update cart');
          cart = await response.json();
        }

        // Update UI
        this.updateCartUI(cart);
        item.classList.remove('is-loading');

        // Reload if quantity is 0 (item removed)
        if (quantity === 0) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to update quantity:', error);
        item.classList.remove('is-loading');
      }
    }

    /**
     * Remove Buttons
     */
    bindRemoveButtons() {
      this.cartPage.querySelectorAll('[data-remove-item]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleRemove(e));
      });
    }

    async handleRemove(e) {
      const btn = e.currentTarget;
      const item = btn.closest('[data-cart-item]');
      const key = btn.dataset.lineKey;

      // Prevent double-clicks - check if already being removed
      if (!item || item.classList.contains('is-removing')) return;
      
      // Mark as being removed and disable button
      item.classList.add('is-removing');
      btn.disabled = true;

      try {
        if (window.CartAPI) {
          await window.CartAPI.removeItem(key);
        } else {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
        }

        // Reload to show updated cart
        window.location.reload();
      } catch (error) {
        console.error('Failed to remove item:', error);
        item.classList.remove('is-removing');
        btn.disabled = false;
      }
    }

    /**
     * Order Notes
     */
    bindNotesToggle() {
      const toggle = this.cartPage.querySelector('[data-notes-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', !expanded);
      });
    }

    bindNotesTextarea() {
      const textarea = this.cartPage.querySelector('[data-cart-note]');
      if (!textarea) return;

      let noteTimeout;
      textarea.addEventListener('input', () => {
        clearTimeout(noteTimeout);
        noteTimeout = setTimeout(() => {
          this.updateNote(textarea.value);
        }, 1000);
      });
    }

    async updateNote(note) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note })
        });
      } catch (error) {
        console.error('Failed to update note:', error);
      }
    }

    /**
     * Offers Toggle
     */
    bindOffersToggle() {
      const offersBtn = this.cartPage.querySelector('[data-offers-toggle]');
      if (!offersBtn) return;

      offersBtn.addEventListener('click', () => {
        // Open offers drawer if available
        document.dispatchEvent(new CustomEvent('offers:open'));
      });

      // Load offers carousel
      this.loadOffersCarousel();
    }

    loadOffersCarousel() {
      const carousel = this.cartPage.querySelector('[data-cart-offers-carousel]');
      if (!carousel) return;

      // Get offers from window.themeOffers (same as cart drawer)
      const offers = window.themeOffers || [];
      
      const placeholder = carousel.querySelector('[data-cart-offers-placeholder]');

      if (offers.length === 0) {
        if (placeholder) {
          const titleEl = placeholder.querySelector('.cart-page__offers-title');
          if (titleEl) titleEl.textContent = 'No offers available';
        }
        return;
      }

      // Hide placeholder
      if (placeholder) {
        placeholder.classList.add('is-hidden');
        setTimeout(() => placeholder.remove(), 400);
      }

      // Check if offers already rendered
      if (carousel.querySelectorAll('.cart-page__offers-item:not([data-cart-offers-placeholder])').length > 0) {
        return;
      }

      // Create offer items
      offers.forEach((offer, index) => {
        const item = document.createElement('div');
        item.className = 'cart-page__offers-item';
        if (index === 0) item.classList.add('is-active');
        item.innerHTML = `
          <span class="cart-page__offers-title">
            ${offer.title || 'Special Offer'}
            ${offer.badge ? `<span class="cart-page__offers-badge">${offer.badge}</span>` : ''}
          </span>
          <span class="cart-page__offers-code">Use code: ${offer.code}</span>
        `;
        carousel.appendChild(item);
      });

      // Start carousel rotation if multiple offers
      if (offers.length > 1) {
        this.startOffersCarousel(carousel, offers.length);
      }
    }

    startOffersCarousel(carousel, count) {
      let currentIndex = 0;
      const items = carousel.querySelectorAll('.cart-page__offers-item:not([data-cart-offers-placeholder])');

      setInterval(() => {
        items[currentIndex].classList.remove('is-active');
        currentIndex = (currentIndex + 1) % count;
        items[currentIndex].classList.add('is-active');
      }, 4000);
    }

    /**
     * Update Cart UI
     */
    updateCartUI(cart) {
      // Update subtotal with compare price if discounts apply
      const subtotalEl = this.cartPage.querySelector('[data-cart-subtotal]');
      if (subtotalEl) {
        if (cart.original_total_price > cart.total_price) {
          subtotalEl.innerHTML = `
            <span class="cart-page__subtotal-current">${this.formatMoney(cart.total_price)}</span>
            <span class="cart-page__subtotal-compare">${this.formatMoney(cart.original_total_price)}</span>
          `;
        } else {
          subtotalEl.textContent = this.formatMoney(cart.total_price);
        }
      }

      // Update item count
      const countEl = this.cartPage.querySelector('[data-cart-page-count]');
      if (countEl) {
        countEl.textContent = `(${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;
      }

      // Update spending goal progress
      this.updateSpendingGoalProgress(cart.total_price);

      // Update discounts section
      this.updateDiscounts(cart);

      // Update individual item prices
      cart.items.forEach(item => {
        const itemEl = this.cartPage.querySelector(`[data-line-key="${item.key}"]`);
        if (itemEl) {
          const priceEl = itemEl.querySelector('[data-item-price]');
          if (priceEl) {
            if (item.original_line_price !== item.final_line_price) {
              priceEl.innerHTML = `
                <span class="cart-page__item-price--sale">${this.formatMoney(item.final_line_price)}</span>
                <span class="cart-page__item-price--compare">${this.formatMoney(item.original_line_price)}</span>
              `;
            } else if (item.original_price > item.final_price) {
              // Variant has compare at price
              priceEl.innerHTML = `
                <span class="cart-page__item-price--sale">${this.formatMoney(item.final_line_price)}</span>
                <span class="cart-page__item-price--compare">${this.formatMoney(item.original_price * item.quantity)}</span>
              `;
            } else {
              priceEl.innerHTML = `<span>${this.formatMoney(item.final_line_price)}</span>`;
            }
          }
        }
      });

      // Reload if cart is empty
      if (cart.item_count === 0) {
        window.location.reload();
      }
    }

    updateSpendingGoalProgress(cartTotal) {
      const progressEl = this.cartPage.querySelector('[data-spending-goal-bar]');
      if (!progressEl) return;

      const threshold = parseInt(progressEl.dataset.threshold) || this.spendingGoalThreshold;
      const goalName = progressEl.dataset.goalName || this.goalName;
      const progressText = progressEl.dataset.progressText || 'Spend [amount] more to unlock [goal]!';
      const successText = progressEl.dataset.successText || 'You\'ve unlocked [goal]!';

      if (!threshold) return;

      const remaining = Math.max(0, threshold - cartTotal);
      const progress = Math.min(100, (cartTotal / threshold) * 100);

      // Update progress fill
      const fillEl = progressEl.querySelector('[data-progress-fill]');
      if (fillEl) {
        fillEl.style.width = `${progress}%`;
      }

      // Update message
      const textEl = progressEl.querySelector('.cart-page__progress-text');
      const messageEl = progressEl.querySelector('[data-progress-message]');

      if (textEl && messageEl) {
        if (remaining > 0) {
          textEl.classList.remove('cart-page__progress-text--success');
          const formattedAmount = this.formatMoney(remaining);
          messageEl.textContent = progressText.replace('[amount]', formattedAmount).replace('[goal]', goalName);
          
          // Update icon to truck
          const iconEl = textEl.querySelector('[data-lucide]');
          if (iconEl) {
            iconEl.setAttribute('data-lucide', 'truck');
            if (typeof lucide !== 'undefined') lucide.createIcons();
          }
        } else {
          textEl.classList.add('cart-page__progress-text--success');
          messageEl.textContent = successText.replace('[goal]', goalName);
          
          // Update icon to check-circle
          const iconEl = textEl.querySelector('[data-lucide]');
          if (iconEl) {
            iconEl.setAttribute('data-lucide', 'check-circle');
            if (typeof lucide !== 'undefined') lucide.createIcons();
          }
        }
      }
    }

    /**
     * Update discounts section
     */
    updateDiscounts(cart) {
      const discountsContainer = this.cartPage.querySelector('[data-applied-discounts]');
      const summaryEl = this.cartPage.querySelector('[data-cart-summary]');
      
      // Get cart-level discounts
      const discounts = cart.cart_level_discount_applications || [];
      
      if (discounts.length === 0) {
        // Hide discounts section if no discounts
        if (discountsContainer) {
          discountsContainer.style.display = 'none';
        }
        return;
      }
      
      // Build discounts HTML
      const discountsHTML = discounts.map(discount => `
        <div class="cart-page__discount-row">
          <span class="cart-page__discount-icon"><span data-lucide="badge-percent"></span></span>
          <span class="cart-page__discount-title">${this.escapeHtml(discount.title)}</span>
          <span class="cart-page__discount-amount">-${this.formatMoney(discount.total_allocated_amount)}</span>
        </div>
      `).join('');
      
      if (discountsContainer) {
        // Update existing container
        discountsContainer.innerHTML = discountsHTML;
        discountsContainer.style.display = '';
      } else if (summaryEl) {
        // Create new container - find a good spot to insert it
        const newDiscountsContainer = document.createElement('div');
        newDiscountsContainer.className = 'cart-page__discounts';
        newDiscountsContainer.setAttribute('data-applied-discounts', '');
        newDiscountsContainer.innerHTML = discountsHTML;
        
        // Insert before subtotal block
        const subtotalBlock = summaryEl.querySelector('.cart-page__subtotal-block');
        if (subtotalBlock) {
          subtotalBlock.parentNode.insertBefore(newDiscountsContainer, subtotalBlock);
        }
      }
      
      // Re-initialize icons
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }

    /**
     * Escape HTML
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Listen for Cart Updates from other components
     */
    listenForCartUpdates() {
      window.addEventListener('cart:updated', (e) => {
        if (e.detail && e.detail.cart) {
          this.updateCartUI(e.detail.cart);
        }
      });
    }

    /**
     * Format Money
     */
    formatMoney(cents) {
      if (window.formatMoney) {
        return window.formatMoney(cents, this.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CartPage());
  } else {
    new CartPage();
  }

  // Expose for external use
  window.CartPage = CartPage;
})();

